import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaDisplay } from "@/components/MediaCard";
import { cn } from "@/utils/cn";
import { CarouselTapZones } from "./CarouselTapZones";
import { CarouselVideoSlide } from "./CarouselVideoSlide";

interface PostCarouselMediaProps {
  currentItem: MediaItem;
  /** Render the current slide as an autoplaying, tappable inline video. */
  isVideoSlide: boolean;
  /** Multi-item carousel — enables left/right navigation zones. */
  showTapZones: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Full-resolution image instead of the thumbnail (used by the expand view). */
  useThumbnail?: boolean;
  /** Video slide starts as a thumbnail, plays on first center tap (grid). */
  lazyVideo?: boolean;
  getStreamUrlByFileId?: (fileId: string) => string | undefined;
  className?: string;
  /** Overlay slots (dots, expand button, badges) painted above the media. */
  children?: React.ReactNode;
}

/**
 * The shared within-post media stage: renders the current slide (image or a
 * focused inline video) inside a fixed 4:5 black frame, overlays the
 * Instagram-style tap zones, and paints any overlay children on top. Used by
 * both the client-review card and its fullscreen expand view.
 */
export const PostCarouselMedia: React.FC<PostCarouselMediaProps> = ({
  currentItem,
  isVideoSlide,
  showTapZones,
  onPrev,
  onNext,
  useThumbnail = true,
  lazyVideo = false,
  getStreamUrlByFileId,
  className,
  children,
}) => {
  return (
    <div className={cn("relative overflow-hidden bg-black", className)}>
      <div className="aspect-4/5">
        <div className="relative h-full w-full">
          {isVideoSlide ? (
            <CarouselVideoSlide
              mediaItem={currentItem}
              getStreamUrlByFileId={getStreamUrlByFileId}
              onPrev={onPrev}
              onNext={onNext}
              showTapZones={showTapZones}
              lazy={lazyVideo}
            />
          ) : (
            <>
              <MediaDisplay
                mediaItem={currentItem}
                useThumbnail={useThumbnail}
                showPlayButton={false}
                className="h-full w-full"
              />
              {showTapZones && (
                <CarouselTapZones mode="image" onPrev={onPrev} onNext={onNext} />
              )}
            </>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
